import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import AppShell from "../components/AppShell";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import {
    Check,
    ChevronLeft,
    Lock,
    Sparkles,
    BookOpen,
    Calculator,
    Coins,
    ArrowRight,
    Loader2,
    Home,
} from "lucide-react";
import { toast } from "sonner";

/** Visual schematic preview for the Standard daily ledger.
 *  Pure CSS — no external image dependency.
 */
function StandardPreview({ t }) {
    return (
        <div
            className="relative h-44 w-full overflow-hidden rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50/70 to-white p-3"
            aria-label={t.dvPreviewAlt}
        >
            <div className="grid h-full grid-cols-3 gap-1.5">
                {[t.dailySalesTitle, t.dailyExpensesTitle, t.dailyPurchasesTitle].map((label, i) => (
                    <div
                        key={label}
                        className="flex flex-col rounded-md border border-amber-100 bg-white/90 p-1.5 shadow-sm"
                    >
                        <div className="truncate rounded-sm bg-amber-700/90 px-1 text-center text-[9px] font-bold text-white">
                            {label}
                        </div>
                        <div className="mt-1 flex-1 space-y-[3px]">
                            {Array.from({ length: 6 }).map((_, k) => (
                                <div
                                    key={k}
                                    className="h-[5px] rounded-sm bg-neutral-200/70"
                                    style={{ width: `${60 + ((i + k) * 7) % 35}%` }}
                                />
                            ))}
                        </div>
                        <div className="mt-1 h-[7px] rounded-sm bg-amber-200/80" />
                    </div>
                ))}
            </div>
        </div>
    );
}

/** Visual schematic preview for the Advanced trading ledger. */
function AdvancedPreview({ t }) {
    return (
        <div
            className="relative h-44 w-full overflow-hidden rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50/70 to-white p-3"
            aria-label={t.dvPreviewAlt}
        >
            <div className="flex h-full flex-col gap-1.5">
                <div className="flex flex-1 flex-col rounded-md border border-indigo-100 bg-white/90 p-1.5 shadow-sm">
                    <div className="truncate rounded-sm bg-indigo-700/90 px-1 text-center text-[9px] font-bold text-white">
                        {t.advSalesTitle}
                    </div>
                    <div className="mt-1 flex flex-1 items-stretch gap-[2px]">
                        {Array.from({ length: 7 }).map((_, c) => (
                            <div key={c} className="flex flex-1 flex-col gap-[2px]">
                                {Array.from({ length: 4 }).map((_, r) => (
                                    <div
                                        key={r}
                                        className="h-[4px] flex-1 rounded-sm bg-indigo-100/80"
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="flex flex-1 flex-col rounded-md border border-emerald-100 bg-white/90 p-1.5 shadow-sm">
                    <div className="truncate rounded-sm bg-emerald-700/90 px-1 text-center text-[9px] font-bold text-white">
                        {t.advPurchasesTitle}
                    </div>
                    <div className="mt-1 flex flex-1 items-stretch gap-[2px]">
                        {Array.from({ length: 6 }).map((_, c) => (
                            <div key={c} className="flex flex-1 flex-col gap-[2px]">
                                {Array.from({ length: 4 }).map((_, r) => (
                                    <div
                                        key={r}
                                        className="h-[4px] flex-1 rounded-sm bg-emerald-100/80"
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function VariantCard({ value, selected, locked, onPick, t, isCurrent }) {
    const isStandard = value === "standard";
    const accent = isStandard
        ? "from-amber-50 to-white border-amber-200"
        : "from-indigo-50 to-white border-indigo-200";
    const ring = selected
        ? isStandard
            ? "ring-2 ring-amber-500"
            : "ring-2 ring-indigo-500"
        : "ring-0";
    const Icon = isStandard ? BookOpen : Sparkles;
    const iconColor = isStandard ? "text-amber-700" : "text-indigo-700";
    return (
        <button
            type="button"
            onClick={onPick}
            disabled={locked}
            data-testid={`dv-card-${value}`}
            className={`group relative flex h-full w-full flex-col overflow-hidden rounded-3xl border bg-gradient-to-br p-5 text-right shadow-sm transition ${accent} ${ring} ${
                locked
                    ? "cursor-not-allowed opacity-95"
                    : "hover:-translate-y-1 hover:shadow-xl"
            }`}
        >
            {isCurrent ? (
                <div
                    className={`absolute top-3 ${
                        isStandard ? "left-3 bg-amber-700" : "left-3 bg-indigo-700"
                    } flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold text-white shadow`}
                    data-testid={`dv-current-badge-${value}`}
                >
                    <Check className="h-3 w-3" /> {t.dvSelectedBadge}
                </div>
            ) : null}

            {isStandard ? <StandardPreview t={t} /> : <AdvancedPreview t={t} />}

            <div className="mt-4 flex items-start gap-3">
                <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/90 ring-1 ring-inset ring-neutral-200`}
                >
                    <Icon className={`h-5 w-5 ${iconColor}`} />
                </div>
                <div className="flex-1">
                    <h3 className="font-heading text-lg font-bold sm:text-xl">
                        {isStandard ? t.dvStandardTitle : t.dvAdvancedTitle}
                    </h3>
                    <p className="mt-1 text-xs leading-relaxed text-neutral-600 sm:text-sm">
                        {isStandard ? t.dvStandardDesc : t.dvAdvancedDesc}
                    </p>
                </div>
            </div>

            <ul className="mt-3 space-y-1.5 text-xs text-neutral-700 sm:text-sm">
                {[
                    isStandard ? t.dvStandardBullet1 : t.dvAdvancedBullet1,
                    isStandard ? t.dvStandardBullet2 : t.dvAdvancedBullet2,
                    isStandard ? t.dvStandardBullet3 : t.dvAdvancedBullet3,
                ].map((b) => (
                    <li key={b} className="flex items-start gap-2">
                        {isStandard ? (
                            <Calculator className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" />
                        ) : (
                            <Coins className="mt-0.5 h-3.5 w-3.5 shrink-0 text-indigo-700" />
                        )}
                        <span>{b}</span>
                    </li>
                ))}
            </ul>

            <div
                className={`mt-4 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition ${
                    selected
                        ? isStandard
                            ? "bg-amber-700 text-white"
                            : "bg-indigo-700 text-white"
                        : "bg-white text-neutral-700 ring-1 ring-neutral-200"
                }`}
            >
                {locked ? (
                    <>
                        <Lock className="h-4 w-4" />
                        {t.dvLockedHint}
                    </>
                ) : selected ? (
                    <>
                        <Check className="h-4 w-4" /> {t.dvSelectedBadge}
                    </>
                ) : (
                    <>
                        {t.dvSelect}
                        <ChevronLeft className="h-4 w-4 transition group-hover:-translate-x-1" />
                    </>
                )}
            </div>
        </button>
    );
}

export default function DailyVariantPicker() {
    const { t } = useLang();
    const navigate = useNavigate();
    const { company, refresh } = useAuth();
    const currentLocked = !!company?.daily_variant;
    const [picked, setPicked] = useState(company?.daily_variant || null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!picked) {
            toast.error(t.dvRequired);
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/set-daily-variant", { variant: picked });
            await refresh?.();
            toast.success(t.dvSavedToast);
            // Support a `?next=` redirect so the post-subscription onboarding
            // can chain: ledger-selection → daily-variant → manager-perms.
            const params = new URLSearchParams(window.location.search);
            const next = params.get("next");
            navigate(next || "/daily", { replace: true });
        } catch (e) {
            toast.error(formatApiError(e) || t.dvSaveFail);
        } finally {
            setSaving(false);
            setConfirmOpen(false);
        }
    };

    return (
        <AppShell>
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3 sm:mb-8">
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">
                        {t.dvKicker}
                    </p>
                    <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">
                        {t.dvTitle}
                    </h1>
                    <p className="mt-1 text-xs text-neutral-500 sm:text-sm">{t.dvSubtitle}</p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="h-10 gap-2"
                    data-testid="dv-back-home-btn"
                >
                    <Home className="h-4 w-4" />
                    {t.dvBackHome}
                </Button>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <VariantCard
                    value="standard"
                    selected={picked === "standard"}
                    locked={currentLocked}
                    isCurrent={company?.daily_variant === "standard"}
                    onPick={() => !currentLocked && setPicked("standard")}
                    t={t}
                />
                <VariantCard
                    value="advanced"
                    selected={picked === "advanced"}
                    locked={currentLocked}
                    isCurrent={company?.daily_variant === "advanced"}
                    onPick={() => !currentLocked && setPicked("advanced")}
                    t={t}
                />
            </div>

            {!currentLocked ? (
                <div className="sticky bottom-4 z-20 mt-6 flex items-center justify-end gap-3 rounded-2xl border border-neutral-200 bg-white/95 p-3 shadow-lg backdrop-blur sm:p-4">
                    <Button
                        type="button"
                        onClick={() => (picked ? setConfirmOpen(true) : toast.error(t.dvRequired))}
                        disabled={saving || !picked}
                        className="h-11 gap-2 bg-amber-700 hover:bg-amber-800"
                        data-testid="dv-confirm-btn"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                        {t.dvSaveBtn}
                    </Button>
                </div>
            ) : (
                <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm text-amber-900">
                    <Lock className="me-2 inline h-4 w-4" />
                    {t.dvLockedHint}
                </div>
            )}

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t.dvConfirmTitle}</AlertDialogTitle>
                        <AlertDialogDescription>{t.dvConfirmBody}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="dv-confirm-cancel">
                            {t.cancel || "إلغاء"}
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={submit} data-testid="dv-confirm-ok">
                            {t.dvSaveBtn}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AppShell>
    );
}
